import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, ShieldCheckIcon } from 'lucide-react';
export function TrippinThroughLifePrivacyPage() {
  return <div className="min-h-screen bg-gradient-to-b from-blush-50 via-white to-peach-50 pt-28 pb-24" data-id="element-872">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-873">
        {/* Back link */}
        <Link to="/apps/trippin-through-life" className="inline-flex items-center gap-2 text-plum-500 hover:text-plum-700 font-medium mb-8 transition-colors" data-id="element-874">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-875" />
          Back to Trippin' Through Life
        </Link>

        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-12" data-id="element-876">
          <div className="w-14 h-14 rounded-2xl bg-blush-100 flex items-center justify-center mx-auto mb-6 shadow-sm" data-id="element-877">
            <ShieldCheckIcon className="w-7 h-7 text-cta-500" data-id="element-878" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-plum-800 mb-3" data-id="element-879">
            Privacy Policy
          </h1>
          <p className="text-plum-500 font-medium" data-id="element-880">
            Trippin' Through Life — Effective May 12, 2026
          </p>
        </motion.div>

        {/* Content */}
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-blush-100" data-id="element-881">
          <div className="prose prose-plum max-w-none text-plum-600 text-sm leading-relaxed space-y-6" data-id="element-882">
            <p data-id="element-883">
              Trippin' Through Life ("we," "our," or "us") respects your privacy
              and is committed to protecting your personal information. This
              Privacy Policy explains how information may be collected, used,
              and protected when using the Trippin' Through Life mobile
              application and related services.
            </p>
            <p data-id="element-884">
              Trippin' Through Life is an AI-powered life management and
              organization app designed to help users manage schedules,
              routines, household tasks, reminders, goals, wellness, travel
              planning, and productivity.
            </p>

            <h3 className="text-plum-800 font-bold text-base mt-8 mb-3" data-id="element-885">
              Information We May Collect
            </h3>
            <p data-id="element-886">Depending on how you use the app, we may collect:</p>
            <ul className="list-disc pl-5 space-y-1" data-id="element-887">
              <li data-id="element-888">Account information such as email address or username</li>
              <li data-id="element-889">Household setup information entered by the user</li>
              <li data-id="element-890">Scheduling, reminder, and checklist data</li>
              <li data-id="element-891">Calendar-related permissions if enabled by the user</li>
              <li data-id="element-892">Notification preferences</li>
              <li data-id="element-893">
                Device information such as device type, operating system, and
                crash reports
              </li>
              <li data-id="element-894">Anonymous analytics and usage information</li>
              <li data-id="element-895">
                Optional location permissions for maps, reminders, or
                travel-related features
              </li>
            </ul>

            <h3 className="text-plum-800 font-bold text-base mt-8 mb-3" data-id="element-896">
              How We Use Information
            </h3>
            <p data-id="element-897">Information collected may be used to:</p>
            <ul className="list-disc pl-5 space-y-1" data-id="element-898">
              <li data-id="element-899">
                Provide app functionality and personalized organization systems
              </li>
              <li data-id="element-900">
                Generate schedules, reminders, routines, and task
                recommendations
              </li>
              <li data-id="element-901">Improve AI-powered planning and productivity features</li>
              <li data-id="element-902">Save preferences, progress, and household settings</li>
              <li data-id="element-903">Improve app stability, usability, and performance</li>
              <li data-id="element-904">Respond to support requests and feedback</li>
              <li data-id="element-905">Prevent abuse or misuse of the platform</li>
            </ul>

            <h3 className="text-plum-800 font-bold text-base mt-8 mb-3" data-id="element-906">
              AI & Automation Features
            </h3>
            <p data-id="element-907">
              Certain features within Trippin' Through Life may use AI-generated
              recommendations, scheduling systems, or automation tools based on
              information voluntarily entered by the user. Users remain
              responsible for reviewing and managing their own schedules,
              reminders, and decisions.
            </p>

            <h3 className="text-plum-800 font-bold text-base mt-8 mb-3" data-id="element-908">
              Data Sharing
            </h3>
            <p data-id="element-909">We do not sell personal information to third parties.</p>
            <p data-id="element-910">
              Some third-party services used by the app, including
              authentication providers, cloud storage, analytics platforms,
              payment processors, advertisements, maps, or notification systems,
              may process limited information required for app functionality.
            </p>

            <h3 className="text-plum-800 font-bold text-base mt-8 mb-3" data-id="element-911">
              Calendar & Notification Permissions
            </h3>
            <p data-id="element-912">
              If enabled by the user, the app may request access to calendar
              functions, notifications, or location-based services in order to
              provide reminders, scheduling tools, travel support, or map
              integrations. These permissions are optional and can typically be
              disabled within device settings.
            </p>

            <h3 className="text-plum-800 font-bold text-base mt-8 mb-3" data-id="element-913">
              User Content
            </h3>
            <p data-id="element-914">
              Checklists, schedules, notes, goals, household information, and
              other content entered into the app may be stored to support
              personalization and saved progress. Users are responsible for the
              information they choose to store within the app.
            </p>

            <h3 className="text-plum-800 font-bold text-base mt-8 mb-3" data-id="element-915">
              Children's Privacy
            </h3>
            <p data-id="element-916">
              Trippin' Through Life is not directed toward children under the
              age of 13. We do not knowingly collect personal information from
              children under 13.
            </p>

            <h3 className="text-plum-800 font-bold text-base mt-8 mb-3" data-id="element-917">
              Data Security
            </h3>
            <p data-id="element-918">
              We take reasonable measures to help protect user information and
              maintain platform security. However, no digital system can
              guarantee complete security.
            </p>

            <h3 className="text-plum-800 font-bold text-base mt-8 mb-3" data-id="element-919">
              Account & Data Removal
            </h3>
            <p data-id="element-920">
              Users may request account or data deletion by contacting: <br data-id="element-921" />
              <strong data-id="element-922">Email:</strong> trippinwithmandy@gmail.com
            </p>

            <h3 className="text-plum-800 font-bold text-base mt-8 mb-3" data-id="element-923">
              Changes to This Policy
            </h3>
            <p data-id="element-924">
              This Privacy Policy may be updated periodically. Continued use of
              the app after updates constitutes acceptance of the revised
              policy.
            </p>

            <h3 className="text-plum-800 font-bold text-base mt-8 mb-3" data-id="element-925">
              Contact
            </h3>
            <p data-id="element-926">
              For questions regarding this Privacy Policy, contact: <br data-id="element-927" />
              <strong data-id="element-928">Trippin' with Mandy Email:</strong>{' '}
              trippinwithmandy@gmail.com
            </p>
          </div>
        </div>
      </div>
    </div>;
}